import { useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { ShoppingCart, Minus, Plus, Trash2, ExternalLink, Loader2, Tag, Truck, Sparkles, X, ChevronRight } from "lucide-react";
import { useCartStore } from "@/stores/cartStore";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { useProducts } from "@/hooks/useProducts";
import { ShopifyProduct } from "@/lib/shopify";
import { trackStartedCheckout } from "@/lib/klaviyo";
import { markCheckoutStarted } from "@/components/ReviewRequestPopup";
import { toast } from "sonner";

// Available discount codes from Shopify
const DISCOUNT_CODES = [
  { code: "SPIN5%", description: "5% rabatt", type: "percentage" },
  { code: "SPIN10%", description: "10% rabatt", type: "percentage" },
  { code: "SPIN15%", description: "15% rabatt", type: "percentage" },
  { code: "SPIN30%", description: "30% rabatt", type: "percentage" },
  { code: "FRIFRAKT", description: "Fri frakt", type: "shipping" },
];

// Quick add component for recommendations
const QuickAddItem = ({ product, onAdd, isLoading }: { 
  product: ShopifyProduct; 
  onAdd: () => void;
  isLoading: boolean;
}) => {
  const price = parseFloat(product.node.priceRange.minVariantPrice.amount);
  const compareAtPrice = product.node.compareAtPriceRange?.minVariantPrice?.amount 
    ? parseFloat(product.node.compareAtPriceRange.minVariantPrice.amount) 
    : null;
  const discount = compareAtPrice && compareAtPrice > price 
    ? Math.round((1 - price / compareAtPrice) * 100) 
    : null;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="flex items-center gap-3 p-3 rounded-xl bg-secondary/50 border border-border/30 hover:border-primary/20 transition-all group"
    >
      <div className="w-16 h-16 rounded-lg bg-muted/30 overflow-hidden flex-shrink-0">
        {product.node.images?.edges?.[0]?.node && (
          <img 
            src={product.node.images.edges[0].node.url} 
            alt={product.node.title} 
            className="w-full h-full object-cover"
          />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground line-clamp-2">{product.node.title}</p>
        <p className="text-sm text-muted-foreground mt-0.5">{price.toFixed(0)} kr</p>
      </div>
      <Button 
        size="sm" 
        variant="outline"
        className="h-9 px-4 text-sm font-medium"
        onClick={onAdd}
        disabled={isLoading}
      >
        Lägg till
      </Button>
    </motion.div>
  );
};



export const CartDrawer = () => {
  const [discountCode, setDiscountCode] = useState("");
  const [appliedCode, setAppliedCode] = useState<string | null>(null);
  const [showDiscountInput, setShowDiscountInput] = useState(false);
  const [addingProductId, setAddingProductId] = useState<string | null>(null);
  
  const { items, isLoading, isSyncing, isOpen, setIsOpen, updateQuantity, removeItem, getCheckoutUrl, syncCart, addItem } = useCartStore();
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = items.reduce((sum, item) => sum + (parseFloat(item.price.amount) * item.quantity), 0);

  // Fetch recommendations
  const { data: allProducts } = useProducts(20);

  // Get recommended products (not in cart)
  const recommendations = useMemo(() => {
    if (!allProducts) return [];
    const cartProductIds = items.map(i => i.product.node.id);
    return allProducts
      .filter(p => !cartProductIds.includes(p.node.id))
      .filter(p => !p.node.title.toLowerCase().includes('pack'))
      .slice(0, 3);
  }, [allProducts, items]);

  useEffect(() => { if (isOpen) syncCart(); }, [isOpen, syncCart]);

  const handleApplyDiscount = () => {
    const code = discountCode.trim().toUpperCase();
    const validCode = DISCOUNT_CODES.find(c => c.code === code);
    if (validCode) {
      setAppliedCode(code);
      setDiscountCode("");
      setShowDiscountInput(false);
    }
  };

  const handleCheckout = () => {
    let checkoutUrl = getCheckoutUrl();
    if (checkoutUrl) {
      // Mark checkout started for review popup
      markCheckoutStarted();
      
      // Track Started Checkout for Klaviyo Abandoned Checkout flow
      trackStartedCheckout({
        checkoutUrl,
        items: items.map(item => ({
          productId: item.product.node.id,
          productName: item.product.node.title,
          productUrl: `${window.location.origin}/product/${item.product.node.handle}`,
          imageUrl: item.product.node.images?.edges?.[0]?.node?.url || '',
          price: parseFloat(item.price.amount),
          quantity: item.quantity,
          variantId: item.variantId,
          variantTitle: item.variantTitle,
        })),
        currencyCode: items[0]?.price.currencyCode || 'SEK',
      });

      if (appliedCode) {
        const url = new URL(checkoutUrl);
        url.searchParams.set('discount', appliedCode);
        checkoutUrl = url.toString();
      }

      // Open checkout in a new tab. In preview/iframes some browsers block popups;
      // fall back to a programmatic anchor click and show a toast with copy action.
      const opened = window.open(checkoutUrl, '_blank', 'noopener,noreferrer');
      if (!opened) {
        try {
          const a = document.createElement('a');
          a.href = checkoutUrl;
          a.target = '_blank';
          a.rel = 'noopener noreferrer';
          document.body.appendChild(a);
          a.click();
          a.remove();
        } catch {
          // no-op, we will show the toast below
        }

        toast.error("Checkout didn’t open", {
          description: "Your browser/preview likely blocked the pop-up. Allow pop-ups, or copy the checkout link.",
          position: "top-center",
          action: {
            label: "Copy link",
            onClick: async () => {
              try {
                await navigator.clipboard.writeText(checkoutUrl!);
                toast.success("Checkout link copied", { position: "top-center" });
              } catch {
                // Clipboard can fail (non-secure context). As a fallback, just try opening again.
                window.open(checkoutUrl!, '_blank', 'noopener,noreferrer');
              }
            },
          },
        });
      }

      setIsOpen(false);
    }
  };

  const handleQuickAdd = async (product: ShopifyProduct) => {
    const variant = product.node.variants.edges[0]?.node;
    if (!variant) return;
    
    setAddingProductId(product.node.id);
    await addItem({
      product,
      variantId: variant.id,
      variantTitle: variant.title,
      price: variant.price,
      quantity: 1,
      selectedOptions: variant.selectedOptions || []
    });
    setAddingProductId(null);
  };

  const freeShippingThreshold = 499;
  const amountToFreeShipping = Math.max(0, freeShippingThreshold - totalPrice);
  const shippingProgress = Math.min(100, (totalPrice / freeShippingThreshold) * 100);
  const hasFreeShipping = amountToFreeShipping === 0;

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button 
          variant="outline" 
          size="icon" 
          className="relative group"
          aria-label={`Varukorg${totalItems > 0 ? `, ${totalItems} produkter` : ''}`}
        >
          <ShoppingCart className="h-5 w-5 transition-transform group-hover:scale-110" />
          <AnimatePresence>
            {totalItems > 0 && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                className="absolute -top-2 -right-2"
              >
                <Badge className="h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs cta-gradient border-0 font-bold">
                  {totalItems}
                </Badge>
              </motion.div>
            )}
          </AnimatePresence>
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-md flex flex-col h-full p-0 border-l border-border/30 bg-background">
        {/* Header - Clean minimal style */}
        <SheetHeader className="flex-shrink-0 p-5 pb-4 border-b border-border/30">
          <div className="flex items-start">
            <div>
              <SheetTitle className="font-semibold text-xl text-foreground">Din varukorg</SheetTitle>
              <p className="text-sm text-muted-foreground mt-0.5">
                {totalItems === 0 ? "Tom" : `${totalItems} produkt${totalItems !== 1 ? 'er' : ''} i din varukorg`}
              </p>
            </div>
          </div>
        </SheetHeader>

        <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
          {items.length === 0 ? (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex-1 flex flex-col items-center justify-center p-8"
            >
              <div className="w-20 h-20 rounded-2xl bg-muted/50 flex items-center justify-center mb-4">
                <ShoppingCart className="h-10 w-10 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-1">Din varukorg är tom</h3>
              <p className="text-sm text-muted-foreground text-center mb-6">
                Utforska våra produkter och hitta något du gillar!
              </p>
              <Button onClick={() => setIsOpen(false)} className="cta-gradient">
                <Sparkles className="w-4 h-4 mr-2" />
                Shoppa nu
              </Button>
            </motion.div>
          ) : (
            <>
              {/* Free Shipping Progress - Clean minimal style */}
              <div className="p-4 border-b border-border/20 bg-secondary/30">
                <div className="flex items-center gap-2 mb-2">
                  {hasFreeShipping ? (
                    <>
                      <span className="text-sm">🎉</span>
                      <span className="text-sm font-medium text-accent">Gratis frakt upplåst!</span>
                    </>
                  ) : (
                    <>
                      <Truck className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm text-foreground">
                        Handla för <strong>{amountToFreeShipping.toFixed(0)} kr</strong> till för fri frakt
                      </span>
                    </>
                  )}
                </div>
                <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                  <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: `${shippingProgress}%` }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className={`h-full rounded-full ${hasFreeShipping ? 'bg-accent' : 'bg-primary'}`}
                  />
                </div>
              </div>

              {/* Scrollable content area */}
              <div className="flex-1 overflow-y-auto">
                {/* Cart Items - Clean minimal cards */}
                <div className="p-4 space-y-3">
                  <AnimatePresence mode="popLayout">
                    {items.map((item, index) => (
                      <motion.div
                        key={item.variantId}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20, height: 0 }}
                        transition={{ delay: index * 0.05 }}
                        layout
                        className="relative flex gap-3 p-3 rounded-xl bg-secondary/30 border border-border/20"
                      >
                        <div className="w-20 h-20 rounded-lg bg-muted/30 overflow-hidden flex-shrink-0">
                          {item.product.node.images?.edges?.[0]?.node && (
                            <img 
                              src={item.product.node.images.edges[0].node.url} 
                              alt={item.product.node.title} 
                              className="w-full h-full object-cover" 
                            />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-sm text-foreground line-clamp-2">
                                {item.product.node.title}
                              </h4>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {item.selectedOptions.map(option => option.value).join(' / ')}
                              </p>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-6 w-6 text-muted-foreground hover:text-destructive flex-shrink-0" 
                              onClick={() => removeItem(item.variantId)}
                              disabled={isLoading}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                          <div className="flex items-center justify-between mt-3">
                            <p className="font-semibold text-foreground">
                              {parseFloat(item.price.amount).toFixed(0)} kr
                            </p>
                            <div className="flex items-center border border-border/50 rounded-lg">
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-8 w-8 rounded-r-none"
                                onClick={() => updateQuantity(item.variantId, item.quantity - 1)}
                                disabled={isLoading}
                              >
                                <Minus className="h-3 w-3" />
                              </Button>
                              <span className="w-8 text-center text-sm font-medium">{item.quantity}</span>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-8 w-8 rounded-l-none"
                                onClick={() => updateQuantity(item.variantId, item.quantity + 1)}
                                disabled={isLoading}
                              >
                                <Plus className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>


                {/* Du kanske också gillar - Clean minimal style */}
                {recommendations.length > 0 && (
                  <div className="px-4 pb-4 border-t border-border/20 pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-foreground">Du kanske också gillar</span>
                      <span className="text-xs text-muted-foreground">Visa alla →</span>
                    </div>
                    <div className="space-y-2">
                      {recommendations.map((product) => (
                        <QuickAddItem 
                          key={product.node.id}
                          product={product}
                          onAdd={() => handleQuickAdd(product)}
                          isLoading={addingProductId === product.node.id}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer - Clean minimal checkout */}
              <div className="flex-shrink-0 border-t border-border/30 bg-background">
                {/* Discount Code Section */}
                <div className="p-4 border-b border-border/20">
                  {!appliedCode && !showDiscountInput ? (
                    <Button 
                      variant="ghost" 
                      className="w-full justify-between text-muted-foreground hover:text-foreground h-10 px-0"
                      onClick={() => setShowDiscountInput(true)}
                    >
                      <span className="flex items-center gap-2">
                        <Tag className="w-4 h-4" />
                        Har du en rabattkod?
                      </span>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  ) : showDiscountInput && !appliedCode ? (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      className="flex gap-2"
                    >
                      <Input
                        value={discountCode}
                        onChange={(e) => setDiscountCode(e.target.value)}
                        placeholder="Ange rabattkod"
                        className="h-10 bg-secondary/50"
                        onKeyDown={(e) => e.key === 'Enter' && handleApplyDiscount()}
                        autoFocus
                      />
                      <Button onClick={handleApplyDiscount} className="h-10 px-4">
                        Använd
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-10 w-10"
                        onClick={() => { setShowDiscountInput(false); setDiscountCode(""); }}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </motion.div>
                  ) : appliedCode && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="flex items-center justify-between p-3 rounded-lg bg-accent/10 border border-accent/20"
                    >
                      <div className="flex items-center gap-2">
                        <Tag className="w-4 h-4 text-accent" />
                        <div>
                          <p className="text-sm font-medium text-accent">{appliedCode}</p>
                          <p className="text-xs text-muted-foreground">
                            {DISCOUNT_CODES.find(c => c.code === appliedCode)?.description}
                          </p>
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        onClick={() => setAppliedCode(null)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </motion.div>
                  )}
                </div>

                {/* Total & Checkout - Pomaya style */}
                <div className="p-4 space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-base font-medium text-foreground">Delsumma</span>
                    <span className="text-xl font-semibold text-foreground">
                      {totalPrice.toFixed(0)} kr
                    </span>
                  </div>
                  
                  {hasFreeShipping && (
                    <p className="text-sm text-accent flex items-center gap-1">
                      <Truck className="w-4 h-4" />
                      Gratis frakt ingår
                    </p>
                  )}
                  
                  {appliedCode && (
                    <p className="text-xs text-center text-muted-foreground">
                      Rabattkod appliceras i kassan
                    </p>
                  )}

                  {/* Single dominant checkout button */}
                  <Button 
                    onClick={handleCheckout} 
                    className="w-full h-14 cta-gradient text-cta-foreground font-bold text-lg" 
                    disabled={items.length === 0 || isLoading || isSyncing}
                  >
                    {isLoading || isSyncing ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        Gå till kassan – {totalPrice.toFixed(0)} kr
                      </>
                    )}
                  </Button>
                  
                  {/* Reassurance text */}
                  <p className="text-xs text-center text-muted-foreground pt-2">
                    🔒 Säker betalning • Snabb leverans
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
